// src/pages/admin/integrations/shopify/components/FlowsTab.tsx
import {
  useMemo,
  useState,
  useEffect,
  useRef,
  type FC,
  type ReactNode,
} from 'react';
import { FlowFormState, HeaderType, ShopifyFlow, Template, ToastFn } from '../../../types/integrations';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import StatusPill from '../../../components/common/StatusPillAndCopyRow';
import { copyText, formatDate } from '../../../util/functions/UniversalFunctions';
import { useShopifyFlows } from '../../../util/hooks/useShopify';


const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const btnCls = 'px-3 py-2 text-sm rounded-lg font-medium';

// ---------- inline modal (kept here to reduce file count) ----------

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

function Modal({ title, onClose, children, footer }: ModalProps) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const handler = (e: KeyboardEvent) =>
      e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 text-xl leading-none"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- flow editor ----------

interface FlowEditorProps {
  flow: ShopifyFlow;
  templates: Template[];
  variables: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
  toast: ToastFn;
}

const FlowEditor: FC<FlowEditorProps> = ({
  flow,
  templates,
  variables,
  onClose,
  onSaved,
  toast,
}) => {
  const [f, setF] = useState<FlowFormState>(() => ({
    enabled: flow.enabled,
    templateName: flow.templateName ?? '',
    templateLanguage: flow.templateLanguage || 'en',
    params: flow.params.length ? flow.params : [],
    headerType: flow.headerType || 'NONE',
    headerUrl: flow.headerUrl || '',
    buttonParam: flow.buttonParam || '',
    delayMinutes: flow.delayMinutes || 0,
  }));
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  const initialUnit =
    f.delayMinutes && f.delayMinutes % 1440 === 0
      ? 1440
      : f.delayMinutes && f.delayMinutes % 60 === 0
      ? 60
      : 1;
  const [delayUnit, setDelayUnit] = useState(initialUnit);

  const tpl = templates.find((t) => t.name === f.templateName);
  const set = <K extends keyof FlowFormState>(k: K, v: FlowFormState[K]) =>
    setF((x) => ({ ...x, [k]: v }));
  const addParam = (k: string) => k && set('params', [...f.params, k]);
  const removeParam = (i: number) =>
    set(
      'params',
      f.params.filter((_, j) => j !== i)
    );
  const move = (i: number, d: number) => {
    const p = [...f.params];
    const j = i + d;
    if (j < 0 || j >= p.length) return;
    [p[i], p[j]] = [p[j], p[i]];
    set('params', p);
  };

  const save = async () => {
    setBusy(true);
    try {
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Save failed', 'bad');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      toast(`Test sent to ${testPhone}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Test failed', 'bad');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      title={flow.label}
      onClose={onClose}
      footer={
        <>
          <label className="flex items-center gap-2 flex-1">
            <ToggleSwitch
              checked={f.enabled}
              label="Automation on"
              onChange={(v) => set('enabled', v)}
            />
            <span className="text-sm">{f.enabled ? 'On' : 'Off'}</span>
          </label>
          <button
            type="button"
            className={`${btnCls} border border-slate-300 text-slate-700 hover:bg-slate-50`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${btnCls} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-600">{flow.trigger}.</p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <strong className="text-sm text-slate-900 flex-1">
              Suggested template
            </strong>
            <StatusPill>{flow.category}</StatusPill>
            <button
              type="button"
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              onClick={async () =>
                toast(
                  (await copyText(flow.suggestedText))
                    ? 'Copied. Create it in Templates and wait for approval.'
                    : 'Copy failed'
                )
              }
            >
              Copy
            </button>
          </div>
          <p className="text-sm text-slate-700">{flow.suggestedText}</p>
          {flow.suggestedButtons && (
            <p className="text-xs text-slate-500 mt-1">
              {flow.suggestedButtons}
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              WhatsApp template
            </span>
            <select
              value={f.templateName}
              onChange={(e) => {
                const t = templates.find((x) => x.name === e.target.value);
                setF((x) => ({
                  ...x,
                  templateName: e.target.value,
                  templateLanguage: t?.language ?? x.templateLanguage,
                }));
              }}
              className={inputCls}
            >
              <option value="">Choose a template…</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                  {t.status !== 'APPROVED'
                    ? ` (${t.status.toLowerCase()})`
                    : ''}
                </option>
              ))}
            </select>
            {tpl && tpl.status !== 'APPROVED' && (
              <span className="text-xs text-amber-600">
                This template is {tpl.status.toLowerCase()}; messages fail until
                it is approved.
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Language code
            </span>
            <input
              value={f.templateLanguage}
              onChange={(e) => set('templateLanguage', e.target.value)}
              className={inputCls}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-slate-700">
            Template values, in order
          </span>
          <div className="flex flex-wrap gap-2">
            {f.params.length === 0 && (
              <span className="text-xs text-slate-500">
                No values. Add one for each {'{{1}}'}, {'{{2}}'} … in the
                template.
              </span>
            )}
            {f.params.map((p, i) => (
              <span
                key={`${p}-${i}`}
                className="inline-flex items-center gap-1 bg-slate-100 rounded-lg px-2 py-1 text-xs"
              >
                <b className="text-slate-900">{`{{${i + 1}}}`}</b>
                <span className="text-slate-600">{variables[p] ?? p}</span>
                <button
                  type="button"
                  aria-label="Move left"
                  className="text-slate-400 hover:text-slate-700 px-0.5"
                  onClick={() => move(i, -1)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Move right"
                  className="text-slate-400 hover:text-slate-700 px-0.5"
                  onClick={() => move(i, 1)}
                >
                  ›
                </button>
                <button
                  type="button"
                  aria-label="Remove"
                  className="text-red-400 hover:text-red-600 px-0.5"
                  onClick={() => removeParam(i)}
                >
                  ×
                </button>
              </span>
            ))}
            <select
              value=""
              onChange={(e) => addParam(e.target.value)}
              aria-label="Add a value"
              className="rounded-lg border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">+ Add value</option>
              {Object.keys(variables).map((v) => (
                <option key={v} value={v}>
                  {variables[v]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Header</span>
            <select
              value={f.headerType}
              onChange={(e) => set('headerType', e.target.value as HeaderType)}
              className={inputCls}
            >
              <option value="NONE">No media header</option>
              <option value="PRODUCT_IMAGE">Product image</option>
              <option value="IMAGE_URL">Fixed image</option>
            </select>
          </label>
          {f.headerType === 'IMAGE_URL' && (
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-slate-700">
                Image address
              </span>
              <input
                value={f.headerUrl}
                onChange={(e) => set('headerUrl', e.target.value)}
                placeholder="https://…"
                className={inputCls}
              />
            </label>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Link button value
            </span>
            <select
              value={f.buttonParam}
              onChange={(e) => set('buttonParam', e.target.value)}
              className={inputCls}
            >
              <option value="">No dynamic button</option>
              {Object.keys(variables).map((v) => (
                <option key={v} value={v}>
                  {variables[v]}
                </option>
              ))}
            </select>
          </label>
          {flow.delayEditable && (
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-slate-700">
                Send after
              </span>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={Math.round(f.delayMinutes / delayUnit)}
                  onChange={(e) =>
                    set(
                      'delayMinutes',
                      Math.max(0, Number(e.target.value || 0)) * delayUnit
                    )
                  }
                  className={`${inputCls} w-24`}
                />
                <select
                  value={delayUnit}
                  onChange={(e) => {
                    const u = Number(e.target.value);
                    set(
                      'delayMinutes',
                      Math.round(f.delayMinutes / delayUnit) * u
                    );
                    setDelayUnit(u);
                  }}
                  className={inputCls}
                >
                  <option value={1}>minutes</option>
                  <option value={60}>hours</option>
                  <option value={1440}>days</option>
                </select>
              </div>
            </label>
          )}
        </div>

        <div className="bg-slate-50 rounded-lg p-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Send a test
            </span>
            <div className="flex gap-2">
              <input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="91XXXXXXXXXX"
                inputMode="tel"
                className={inputCls}
              />
              <button
                type="button"
                className={`${btnCls} border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50`}
                disabled={!testPhone || !f.templateName || testing}
                onClick={() => void test()}
              >
                {testing ? 'Sending…' : 'Send test'}
              </button>
            </div>
            <span className="text-xs text-slate-500">
              Uses sample values. Charged like any template message.
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
};

// ---------- main tab ----------

interface Props {
  connected: boolean;
  toast: ToastFn;
}

const FlowsTab: FC<Props> = ({ connected, toast }) => {
  const { data, templates, saveFlow } = useShopifyFlows();
  const [editing, setEditing] = useState<ShopifyFlow | null>(null);

  const groups = useMemo<Array<[string, ShopifyFlow[]]>>(() => {
    const g = new Map<string, ShopifyFlow[]>();
    (data?.flows ?? []).forEach((f) => {
      if (!g.has(f.group)) g.set(f.group, []);
      g.get(f.group)!.push(f);
    });
    return Array.from(g.entries());
  }, [data]);

  const quickToggle = async (f: ShopifyFlow, on: boolean) => {
    if (on && !f.templateName) {
      setEditing(f);
      return;
    }
    await saveFlow(f.key, { ...f, enabled: on });
    toast(on ? 'Automation on' : 'Automation off');
  };

  if (!data) return <div className="h-48 rounded-xl bg-slate-200 animate-pulse" />;

  return (
    <div className="flex flex-col gap-5">
      {!connected && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          Connect Shopify first. You can set up the messages now; they start
          once connected.
        </div>
      )}

      {groups.map(([group, flows]) => (
        <section
          key={group}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        >
          <h2 className="px-4 py-3 font-semibold text-slate-900 border-b border-slate-200 bg-slate-50">
            {group}
          </h2>
          <ul className="divide-y divide-slate-100">
            {flows.map((f) => (
              <li key={f.key} className="flex items-center gap-3 px-4 py-3">
                <button
                  type="button"
                  className="flex-1 flex flex-col gap-0.5 text-left"
                  onClick={() => setEditing(f)}
                >
                  <strong className="text-sm text-slate-900">{f.label}</strong>
                  <span className="text-xs text-slate-500">
                    {f.trigger} · {formatDate(f.delayMinutes)}
                  </span>
                  <span className="text-xs">
                    {f.templateName ? (
                      <>
                        Template{' '}
                        <code className="bg-slate-100 px-1 rounded">
                          {f.templateName}
                        </code>
                      </>
                    ) : (
                      <span className="text-amber-600">No template chosen</span>
                    )}
                  </span>
                </button>
                <ToggleSwitch
                  checked={f.enabled}
                  label={`Turn ${f.label} ${f.enabled ? 'off' : 'on'}`}
                  onChange={(on) => void quickToggle(f, on)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {editing && (
        <FlowEditor
          flow={editing}
          templates={templates}
          variables={data.variables}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast('Saved');
          }}
          toast={toast}
        />
      )}
    </div>
  );
};

export default FlowsTab;